import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useRef,
  useState,
} from 'react';
import { ModuleStatus } from 'types/ModuleStatus';
import { MODULE_STATUS } from 'constants/modules';
import {
  AuthorizationErrorResponse,
  SocialLoginBusiness,
  SocialLoginDoneResponse,
  SocialLoginTokenType,
} from 'types/SocialLogin';
import { SocialLoginError } from 'constants/socialLogin';
import Logger from 'utils/logger';
import SocialLoginApi from 'app/modules/socialLogin/api';
import { useQuery } from 'react-query';
import {
  getDefaultScopesByPageType,
  isMetaSafeConnectEligible,
} from 'app/modules/socialLogin/utils';
import { queryKey } from 'constants/queryKey';
import TimeUtils from 'app/utils/time';
import useLocalStorage from 'utils/hooks/useLocalStorage';
import { StringParam, useQueryParams } from 'use-query-params';
import { BrowserStorage } from 'utils/browserStorage';
import HeapUtils from 'app/utils/heap';
import { ApiClientError } from 'api/client';
import MetaTokenTypeModal from 'app/modules/socialLogin/components/MetaTokenTypeModal';
import {
  connectTracking,
  getConnectMethod,
  trackConnectOauthReturned,
} from 'app/modules/socialLogin/utils/connectTracking';

type AuthorizeOptions = {
  pageId?: number;
  pageType: number;
  scopes?: string[];
  redirectUrl?: string;
  tokenType?: SocialLoginTokenType;
};

type SocialLoginContextValue = {
  status: ModuleStatus;
  authorization: SocialLoginDoneResponse;
  error: AuthorizationErrorResponse | undefined;
  isSuccess: boolean;
  isLoading: boolean;
  isError: boolean;
  pageType: number | null;
  userKey: string | null | undefined;
  clientBusinessId: string | null;
  isSubmittingToken: boolean;
  businesses: SocialLoginBusiness[] | undefined;
  activeTokenType: SocialLoginTokenType | undefined;
  authorize: (options: AuthorizeOptions) => void;
  submitSystemUserToken: (
    pageType: number,
    accessToken: string,
  ) => Promise<{ userKey: string; clientBusinessId: string } | undefined>;
  reset: () => void;
  isChoosingMetaTokenType: boolean;
  confirmTokenType: (tokenType: SocialLoginTokenType) => Promise<void>;
  cancelPendingAuthorize: () => void;
};

const SocialLoginContext = createContext<SocialLoginContextValue | undefined>(
  undefined,
);

const SocialLoginContextProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [status, setStatus] = useState<ModuleStatus>(MODULE_STATUS.Idle);
  const [authorization, setAuthorization] = useState<any>();
  const [error, setError] = useState<AuthorizationErrorResponse | undefined>();
  const [pageType, setPageType] = useLocalStorage<number | null>(
    BrowserStorage.keys.SocialLoginPageType,
    null,
  );
  const [query, setQuery] = useQueryParams({
    userKey: StringParam,
  });
  const [clientBusinessId, setClientBusinessId] = useState<string | null>(null);
  const [isSubmittingToken, setIsSubmittingToken] = useState(false);
  const [businesses, setBusinesses] = useState<
    SocialLoginBusiness[] | undefined
  >();
  const [activeTokenType, setActiveTokenType] = useState<
    SocialLoginTokenType | undefined
  >();
  const [pendingAuthorize, setPendingAuthorize] =
    useState<AuthorizeOptions | null>(null);

  const { isLoading: isFetchingPages } = useQuery(
    queryKey.socialLoginPages(query.userKey as string),
    () => SocialLoginApi.fetchPages(query.userKey as string),
    {
      refetchInterval: () => {
        return TimeUtils.calcMilliseconds.seconds(1);
      },
      refetchIntervalInBackground: true,
      retry: 1,
      enabled:
        !!query.userKey &&
        (status === MODULE_STATUS.Loading || status === MODULE_STATUS.Idle),
      onSuccess: (data) => {
        Logger.log('Recieved social login data', data);

        if (data) {
          // Authoritative per fetch: a classic (user-token) flow returns no
          // clientBusinessId, which must CLEAR any value left over from a prior
          // safe-connect. Otherwise the next page save would carry the stale
          // clientBusinessId, get misrouted to the system-user (SUAT) path and
          // be wrongly badged "safe connect".
          setClientBusinessId(data.clientBusinessId ?? null);

          if (data.status === 'done') {
            setStatus(MODULE_STATUS.Succeeded);
            setAuthorization({
              data: data.pages,
              partial: data.partial,
            });
            setBusinesses(data.businesses);
            trackConnectOauthReturned('success');
          }
        }
      },
      onError: (error: ApiClientError) => {
        const errorResponse = {
          reason: error?.userMessage,
          authorizationOptions: authorizationOptions.current,
        };
        Logger.error(errorResponse);
        setError(errorResponse);
        setStatus(MODULE_STATUS.Failed);
        HeapUtils.track(HeapUtils.events.SOCIAL_LOGIN_FETCH_DATA_ERROR, {
          page_type: pageType,
          scopes: authorizationOptions.current.scopes.join(','),
          error_reason: error?.message
            ? error.message
            : error?.userMessage ?? 'Unknown error',
        });
        trackConnectOauthReturned('error', error);
      },
    },
  );
  const authorizationOptions = useRef<{
    pageType: number | null;
    scopes: string[];
  }>({
    pageType: null,
    scopes: [],
  });
  const authorizeAttempt = useRef(0);

  async function authorize(options: AuthorizeOptions) {
    if (
      options.tokenType == null &&
      isMetaSafeConnectEligible(options.pageType)
    ) {
      setPendingAuthorize(options);
      return;
    }

    await runAuthorize(options);
  }

  async function runAuthorize(options: AuthorizeOptions) {
    const attempt = ++authorizeAttempt.current;
    const isCancelled = () => attempt !== authorizeAttempt.current;

    try {
      // A new authorization is a fresh connection flow. Drop any clientBusinessId
      // left over from a previous safe-connect so a user-token (classic) connect
      // cannot inherit it; the system-user and OBO flows repopulate it from fetchPages.
      if (
        options.tokenType !== 'system_user' &&
        options.tokenType !== 'system_user_obo'
      ) {
        setClientBusinessId(null);
      }
      setBusinesses(undefined);
      setActiveTokenType(options.tokenType);

      const scopes = options.scopes
        ? options.scopes
        : getDefaultScopesByPageType(options.pageType);

      authorizationOptions.current.pageType = options?.pageType;
      authorizationOptions.current.scopes = scopes;

      if (!authorizationOptions.current.pageType) {
        throw new Error('Page type not defined');
      }

      setStatus(MODULE_STATUS.Loading);

      const socialLoginUrlResponse = await SocialLoginApi.requestLoginUrl(
        authorizationOptions.current.pageType,
        {
          scopes: authorizationOptions.current?.scopes,
          redirectUrl: options.redirectUrl || window.location.href,
          tokenType: options.tokenType,
        },
      );

      if (isCancelled()) {
        return;
      }

      setPageType(options.pageType);
      const connectAttempt = connectTracking.startOauth(
        options.pageType,
        getConnectMethod(options.pageType, options.tokenType),
      );
      HeapUtils.track(HeapUtils.events.CONNECT_OAUTH_STARTED, {
        network: connectAttempt.network,
        method: connectAttempt.method ?? null,
      });
      window.location.href = socialLoginUrlResponse.loginUrl;
    } catch (e) {
      if (isCancelled()) {
        return;
      }

      const error = {
        reason:
          (e instanceof Error && e.message) ||
          SocialLoginError.unableToAuthorize,
        authorizationOptions: authorizationOptions.current,
      };

      Logger.error(error);
      setError(error);
      setStatus(MODULE_STATUS.Failed);
      HeapUtils.track(HeapUtils.events.SOCIAL_LOGIN_AUTHORIZE_ERROR, {
        page_type: options.pageType,
        scopes: authorizationOptions.current.scopes.join(','),
        error_reason: error.reason,
      });
    }
  }

  async function confirmTokenType(tokenType: SocialLoginTokenType) {
    if (!pendingAuthorize) {
      return;
    }
    const options = pendingAuthorize;
    setPendingAuthorize(null);
    await runAuthorize({ ...options, tokenType });
  }

  function cancelPendingAuthorize() {
    setPendingAuthorize(null);
  }

  async function submitSystemUserToken(
    pageTypeToSubmit: number,
    accessToken: string,
  ) {
    try {
      setIsSubmittingToken(true);
      setError(undefined);
      authorizationOptions.current.pageType = pageTypeToSubmit;
      authorizationOptions.current.scopes =
        getDefaultScopesByPageType(pageTypeToSubmit);

      setStatus(MODULE_STATUS.Loading);

      const response = await SocialLoginApi.submitSystemUserToken(
        pageTypeToSubmit,
        { accessToken },
      );

      setPageType(pageTypeToSubmit);
      setClientBusinessId(response.clientBusinessId);
      setQuery({ userKey: response.userKey });

      return response;
    } catch (e) {
      const errorResponse = {
        reason:
          (e instanceof ApiClientError && e.userMessage) ||
          (e instanceof Error && e.message) ||
          SocialLoginError.unableToAuthorize,
        authorizationOptions: authorizationOptions.current,
      };

      Logger.error(errorResponse);
      setError(errorResponse);
      setStatus(MODULE_STATUS.Failed);
      HeapUtils.track(HeapUtils.events.SOCIAL_LOGIN_AUTHORIZE_ERROR, {
        page_type: pageTypeToSubmit,
        scopes: authorizationOptions.current.scopes.join(','),
        error_reason: errorResponse.reason,
      });

      return undefined;
    } finally {
      setIsSubmittingToken(false);
    }
  }

  const reset = () => {
    authorizeAttempt.current += 1;
    setStatus(MODULE_STATUS.Idle);
    setPageType(null);
    setAuthorization(undefined);
    setError(undefined);
    setClientBusinessId(null);
    setPendingAuthorize(null);
    setBusinesses(undefined);
    setActiveTokenType(undefined);
  };

  return (
    <SocialLoginContext.Provider
      value={{
        status,
        authorization,
        error,
        pageType,
        userKey: query.userKey,
        clientBusinessId,
        isSubmittingToken,
        businesses,
        activeTokenType,
        isSuccess: status === MODULE_STATUS.Succeeded,
        isLoading: status === MODULE_STATUS.Loading || isFetchingPages,
        isError: status === MODULE_STATUS.Failed,
        authorize,
        submitSystemUserToken,
        reset,
        isChoosingMetaTokenType: pendingAuthorize !== null,
        confirmTokenType,
        cancelPendingAuthorize,
      }}
    >
      {children}
      <MetaTokenTypeModal
        open={pendingAuthorize !== null}
        onClose={cancelPendingAuthorize}
        onSelectTokenType={confirmTokenType}
      />
    </SocialLoginContext.Provider>
  );
};

export default SocialLoginContextProvider;

export { SocialLoginContextProvider };

export const useSocialLoginContext = () => {
  const context = useContext(SocialLoginContext);

  if (!context) {
    throw new Error(
      'useSocialLoginContext must be used within a SocialLoginContextProvider',
    );
  }

  return context;
};
